'use client'

import { useEffect, useRef } from 'react'
import { useLoanComparatorStore } from '@/app/stores/loan/loan-comparator.store'
import { ILoanQuote } from '@/app/models/loanQuote'
import { useTracking } from '@/app/contexts/tracking-context'
import { AeTrackingEvents } from '@/app/constants/loanAeTracking'
import { buildAeParams } from '@/app/utils/loan/loan-ae-tracking'
import YesNoSwitch from '@/app/components/atomic/molecules/YesNoSwitch'
import LoanFormField from '@/app/components/loanComparator/fields/LoanFormField'
import AppSelect from '@/app/components/atomic/AppSelect'

export type BankCoverageAnswer = 'bank' | 'external' | null

// Miroir de la validation du parcours : pour un changement d'assurance le choix banque/externe
// est requis, et l'assureur actuel l'est en cas d'assurance externe
export const isBankCoverageAnswerComplete = (answer: BankCoverageAnswer, quote: ILoanQuote | null): boolean => {
    if (answer === 'bank') return true
    if (answer === 'external') return !!quote?.current_insurance_company
    return false
}

interface LoanComparatorBankCoverageQuestionProps {
    value: BankCoverageAnswer
    onChange: (value: BankCoverageAnswer) => void
    error: string | null
    isActive: boolean
}

export default function LoanComparatorBankCoverageQuestion({
    value,
    onChange,
    error,
    isActive,
}: LoanComparatorBankCoverageQuestionProps) {
    const quote = useLoanComparatorStore((state) => state.quote)
    const quoteActions = useLoanComparatorStore((state) => state.quoteActions)
    const globalOptions = useLoanComparatorStore((state) => state.globalOptions)
    const { track } = useTracking()

    // L'écran reste monté quand il n'est pas affiché : les vues se déclenchent sur isActive, pas au mount.
    const trackedViewsRef = useRef({ coverage: false, currentInsurer: false })
    useEffect(() => {
        if (!isActive) return

        const aeParams = buildAeParams(quote)
        if (!trackedViewsRef.current.coverage) {
            trackedViewsRef.current.coverage = true
            track({ event: AeTrackingEvents.coverage, ...aeParams })
        }
        if (value === 'external' && !trackedViewsRef.current.currentInsurer) {
            trackedViewsRef.current.currentInsurer = true
            track({ event: AeTrackingEvents.currentInsurer, ...aeParams })
        }
    }, [isActive, value, quote, track])

    // Mêmes side-effects que selectInsuredByBank / selectInsuredByExternal du parcours
    const handleAnswerChange = (coveredByBank: boolean) => {
        if (coveredByBank) {
            quoteActions.update({ delegation: false })
            onChange('bank')
        } else {
            quoteActions.update({ delegation: true })
            onChange('external')
        }
    }

    return (
        <div className="mb-6">
            <div className="flex flex-col gap-5">
                <LoanFormField
                    label="Êtes-vous actuellement couvert par l'assurance proposée par votre banque ?*"
                    tooltip={{
                        title: 'Votre assurance actuelle',
                        text: "Indiquez si vous avez souscrit le contrat-groupe proposé par votre banque ou un contrat d'assurance en dehors de votre banque.",
                    }}
                    tooltipId="bank-coverage-question"
                    row="switch"
                    labelId="bank-coverage-label"
                    error={error && value === null ? { message: error } : undefined}
                >
                    <YesNoSwitch
                        value={value === null ? null : value === 'bank'}
                        onChange={handleAnswerChange}
                        ariaLabelledBy="bank-coverage-label"
                    />
                </LoanFormField>

                {value === 'external' && (
                    <LoanFormField
                        label="Sélectionnez votre assureur actuel*"
                        inline={false}
                        error={error && !quote?.current_insurance_company ? { message: error } : undefined}
                    >
                        <AppSelect
                            id="current-insurance-company"
                            label=""
                            variant="v2"
                            placeholder="Choisissez..."
                            options={(globalOptions?.externalInsurances ?? []).map((insurer) => ({
                                label: insurer.name,
                                value: insurer.name,
                            }))}
                            value={quote?.current_insurance_company ?? ''}
                            onChange={(e) => quoteActions.update({ current_insurance_company: String(e.target.value) })}
                        />
                    </LoanFormField>
                )}
            </div>
        </div>
    )
}
